import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { membersAPI, paymentsAPI, settingsAPI, supplementsAPI } from '../../utils/api';
import { formatDate, formatCurrency, daysUntilExpiry } from '../../utils/helpers';
import { formatNepaliDate, adToBS, NEPALI_MONTHS } from '../../utils/nepaliDate';
import StatusBadge from '../../components/StatusBadge';
import MemberAvatar from '../../components/MemberAvatar';
import Modal from '../../components/Modal';
import LoadingSpinner from '../../components/LoadingSpinner';
import toast from 'react-hot-toast';

// Show a single payment as a rich card
function PaymentCard({ payment, index, onSettle }) {
  const bs = adToBS(payment.paidDate);
  const bsMonthYear = bs ? `${NEPALI_MONTHS[bs.month]} ${bs.year}` : '';
  const bsDay = bs ? String(bs.day).padStart(2, '0') : '';

  return (
    <div className={`flex flex-col sm:flex-row sm:items-center gap-4 p-4 rounded-xl border-l-4 ${
      payment.status === 'Paid'    ? 'bg-green-50 border-green-500' :
      payment.status === 'Partial' ? 'bg-yellow-50 border-yellow-400' :
      'bg-red-50 border-red-400'
    }`}>
      {/* Date bubble */}
      <div className="flex-shrink-0 flex flex-col items-center justify-center w-16 h-16 rounded-xl bg-white shadow-sm border border-gray-100 text-center">
        <span className="text-xs font-semibold text-gray-500">{bsMonthYear.split(' ')[0]}</span>
        <span className="text-xl font-bold text-gray-800 leading-tight">{bsDay}</span>
        <span className="text-xs text-gray-400">{bs?.year}</span>
      </div>

      {/* Details */}
      <div className="flex-1 min-w-0">
        <div className="flex flex-wrap items-center gap-2 mb-1">
          <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${
            payment.status === 'Paid'    ? 'bg-green-100 text-green-700' :
            payment.status === 'Partial' ? 'bg-yellow-100 text-yellow-700' :
            'bg-red-100 text-red-700'
          }`}>{payment.status}</span>
          <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full">{payment.paymentMode}</span>
          {index === 0 && <span className="text-xs bg-blue-100 text-blue-600 px-2 py-0.5 rounded-full">Latest</span>}
        </div>
        <p className="text-sm text-gray-500">
          {formatNepaliDate(payment.paidDate)}
        </p>
        {payment.notes && (
          <p className="text-xs text-gray-400 mt-0.5 italic truncate">{payment.notes}</p>
        )}
        {payment.nextDueDate && (
          <p className="text-xs text-gray-400 mt-0.5">
            Next due: <span className="font-medium text-gray-600">{formatNepaliDate(payment.nextDueDate)}</span>
          </p>
        )}
      </div>

      {/* Amount + settle button */}
      <div className="text-right flex-shrink-0 flex flex-col items-end gap-1">
        <p className="text-xl font-bold text-gray-800">{formatCurrency(payment.amountPaid)}</p>
        {payment.amount !== payment.amountPaid && (
          <p className="text-xs text-gray-500">of {formatCurrency(payment.amount)}</p>
        )}
        {payment.balanceRemaining > 0 && (
          <p className="text-xs text-red-500 font-medium">
            Balance: {formatCurrency(payment.balanceRemaining)}
          </p>
        )}
        {payment.status === 'Partial' && onSettle && (
          <button
            onClick={() => onSettle(payment)}
            className="mt-1 text-xs px-3 py-1 bg-yellow-500 hover:bg-yellow-600 text-white rounded-lg font-medium transition-colors"
          >
            Pay Balance
          </button>
        )}
      </div>
    </div>
  );
}

function MemberProfile() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [pricing, setPricing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [renewModal, setRenewModal] = useState(false);
  const [quickPayModal, setQuickPayModal] = useState(false);
  const [calcData, setCalcData] = useState(null);
  const [payMode, setPayMode] = useState('Cash');
  const [discount, setDiscount] = useState('0');
  const [isPartial, setIsPartial] = useState(false);
  const [partialAmount, setPartialAmount] = useState('');
  const [renewForm, setRenewForm] = useState({ plan: 'Monthly', paymentMode: 'Cash', amount: '' });

  // Settle partial payment state
  const [settleModal, setSettleModal]         = useState(false);
  const [settlePayment, setSettlePayment]     = useState(null);
  const [settleAmount, setSettleAmount]       = useState('');
  const [settleMode, setSettleMode]           = useState('Cash');
  const [settleLoading, setSettleLoading]     = useState(false);

  // Supplement sale state
  const [supModal, setSupModal]       = useState(false);
  const [supplements, setSupplements] = useState([]);
  const [supForm, setSupForm]         = useState({ supplementId: '', quantity: '1', discount: '0' });
  const [supLoading, setSupLoading]   = useState(false);

  const fetchData = async () => {
    try {
      const [memberRes, priceRes] = await Promise.all([
        membersAPI.getOne(id),
        settingsAPI.getPricing()
      ]);
      setData(memberRes.data);
      setPricing(priceRes.data);
    } catch {
      toast.error('Failed to load member');
      navigate('/members');
    } finally {
      setLoading(false);
    }
  };

  const fetchCalc = async () => {
    try {
      const res = await paymentsAPI.calculate(id);
      setCalcData(res.data);
    } catch {}
  };

  const openSupModal = async () => {
    try {
      const res = await supplementsAPI.getAll();
      setSupplements(res.data.filter(s => s.stock > 0));
      setSupForm({ supplementId: '', quantity: '1', discount: '0' });
      setSupModal(true);
    } catch {
      toast.error('Failed to load supplements');
    }
  };

  const openSettle = (payment) => {
    setSettlePayment(payment);
    setSettleAmount(String(payment.balanceRemaining));
    setSettleMode('Cash');
    setSettleModal(true);
  };

  const handleSettle = async () => {
    if (!settleAmount || parseFloat(settleAmount) <= 0) { toast.error('Enter a valid amount'); return; }
    setSettleLoading(true);
    try {
      await paymentsAPI.settle(settlePayment._id, {
        additionalAmount: parseFloat(settleAmount),
        paymentMode: settleMode,
      });
      const fullyPaid = parseFloat(settleAmount) >= settlePayment.balanceRemaining;
      toast.success(fullyPaid ? '✅ Balance cleared! Expiry extended.' : '💰 Partial payment recorded.');
      setSettleModal(false);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to settle payment');
    } finally {
      setSettleLoading(false);
    }
  };

  const handleSupSell = async () => {
    if (!supForm.supplementId) { toast.error('Please select a supplement'); return; }
    setSupLoading(true);
    try {
      const qty = parseInt(supForm.quantity) || 1;
      const discount = parseFloat(supForm.discount) || 0;
      await supplementsAPI.sell({
        supplementId: supForm.supplementId,
        buyerType: 'Member',
        memberId: id,
        quantity: qty,
        discount,
      });
      const selectedSup = supplements.find(s => s._id === supForm.supplementId);
      const total = Math.max(0, (selectedSup?.sellingPrice || 0) * qty - discount);
      toast.success(`💊 Sale recorded! ${formatCurrency(total)} collected`);
      setSupModal(false);
      fetchData(); // refresh supplement purchase count
    } catch (err) {
      toast.error(err.response?.data?.message || 'Sale failed');
    } finally {
      setSupLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, [id]);

  const openQuickPay = () => { fetchCalc(); setIsPartial(false); setPartialAmount(''); setQuickPayModal(true); };

  const handleQuickPay = async () => {
    try {
      const disc = parseFloat(discount) || 0;
      const payload = { memberId: id, paymentMode: payMode, discount: disc };
      if (isPartial) {
        const paid = parseFloat(partialAmount);
        if (!paid || paid <= 0) { toast.error('Enter a valid partial amount'); return; }
        payload.amountPaid = paid;
      }
      const res = await paymentsAPI.quickPay(payload);
      if (res.data.isPartial) {
        toast.success(`💰 Partial payment of ${formatCurrency(res.data.payment.amountPaid)} recorded.`, { duration: 4000 });
      } else {
        const expLabel = res.data.newExpiry ? ` • Expires: ${formatNepaliDate(res.data.newExpiry)}` : '';
        toast.success(`✅ ${formatCurrency(res.data.amount)} collected!${expLabel}`, { duration: 4000 });
      }
      setQuickPayModal(false);
      setDiscount('0');
      setIsPartial(false);
      setPartialAmount('');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Payment failed');
    }
  };

  const handleFreeze = async () => {
    try {
      await membersAPI.freeze(id);
      toast.success(data.member.status === 'Frozen' ? 'Membership unfrozen' : 'Membership frozen');
      fetchData();
    } catch { toast.error('Action failed'); }
  };

  const handleRenew = async () => {
    try {
      await membersAPI.renew(id, renewForm);
      toast.success('Membership renewed!');
      setRenewModal(false);
      fetchData();
    } catch { toast.error('Renewal failed'); }
  };

  if (loading) return <LoadingSpinner />;
  if (!data) return null;

  const { member, payments, attendance, supplementSales } = data;
  const presentDays = attendance.filter(a => a.checkedIn).length;
  const totalCollected = payments.reduce((s, p) => s + p.amountPaid, 0)
                       + supplementSales.reduce((s, sale) => s + (sale.totalAmount || 0), 0);
  const days = daysUntilExpiry(member.expiryDate);
  const bsJoining = formatNepaliDate(member.joiningDate);
  const bsExpiry  = formatNepaliDate(member.expiryDate);

  const quickPayable = calcData
    ? Math.max(0, calcData.total - (parseFloat(discount) || 0))
    : 0;
  const showDiscountInModal = member.plan !== 'Monthly';

  return (
    <div className="max-w-4xl mx-auto space-y-5">
      {/* Back */}
      <div className="flex items-center gap-4">
        <button onClick={() => navigate('/members')} className="p-2 hover:bg-gray-100 rounded-lg text-gray-600">← Back</button>
        <h2 className="text-2xl font-bold text-gray-800">Member Profile</h2>
      </div>

      {/* Profile Card */}
      <div className="card">
        <div className="flex flex-col sm:flex-row gap-6">
          <MemberAvatar photo={member.photo} name={member.name} size="xl" />
          <div className="flex-1">
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div>
                <h3 className="text-2xl font-bold text-gray-800">{member.name}</h3>
                <p className="text-gray-500 font-mono text-sm">{member.memberId}</p>
                <div className="flex items-center gap-3 mt-2 flex-wrap">
                  <StatusBadge status={member.status} />
                  <span className="text-sm text-gray-500">{member.plan} • {member.shift}</span>
                  <span className="text-sm text-gray-500">{member.gender}, {member.age} yrs</span>
                </div>
              </div>
              <div className="flex flex-wrap gap-2">
                <button onClick={openQuickPay} className="btn-success text-sm">✓ Mark Paid</button>
                <button onClick={openSupModal}
                  className="text-sm px-4 py-2 rounded-lg font-medium bg-purple-100 text-purple-700 hover:bg-purple-200 transition-colors">
                  💊 Buy Supplement
                </button>
                <button onClick={handleFreeze}
                  className={`text-sm px-4 py-2 rounded-lg font-medium transition-colors ${member.status === 'Frozen' ? 'bg-blue-100 text-blue-700 hover:bg-blue-200' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}>
                  {member.status === 'Frozen' ? '🔓 Unfreeze' : '🔒 Freeze'}
                </button>
                <button onClick={() => setRenewModal(true)} className="btn-primary text-sm">🔄 Renew</button>
                <button onClick={() => navigate(`/members/edit/${id}`)} className="btn-secondary text-sm">✏️ Edit</button>
              </div>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-5">
              <div className="bg-gray-50 rounded-lg p-3">
                <p className="text-xs text-gray-500">Phone</p>
                <p className="text-sm font-medium text-gray-800 mt-0.5">{member.phone}</p>
              </div>
              <div className="bg-gray-50 rounded-lg p-3">
                <p className="text-xs text-gray-500">Joined</p>
                <p className="text-sm font-medium text-gray-800 mt-0.5">{bsJoining}</p>
              </div>
              <div className={`rounded-lg p-3 ${days <= 7 && member.status !== 'Frozen' ? 'bg-yellow-50' : 'bg-gray-50'}`}>
                <p className="text-xs text-gray-500">Expires</p>
                <p className="text-sm font-medium text-gray-800 mt-0.5">{bsExpiry}</p>
                {member.status !== 'Frozen' && member.status !== 'Expired' && (
                  <p className={`text-xs mt-0.5 font-semibold ${days <= 3 ? 'text-red-600' : days <= 7 ? 'text-yellow-600' : 'text-gray-400'}`}>
                    {days}d left
                  </p>
                )}
              </div>
              <div className="bg-gray-50 rounded-lg p-3">
                <p className="text-xs text-gray-500">Emergency</p>
                <p className="text-sm font-medium text-gray-800 mt-0.5">{member.emergencyContact?.name || '-'}</p>
                <p className="text-xs text-gray-400">{member.emergencyContact?.phone || ''}</p>
              </div>
            </div>

            {member.healthNotes && (
              <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                <p className="text-xs font-semibold text-yellow-700 mb-1">⚠️ Health Notes</p>
                <p className="text-sm text-yellow-900">{member.healthNotes}</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="card text-center">
          <p className="text-3xl font-bold text-blue-600">{presentDays}</p>
          <p className="text-sm text-gray-500 mt-1">Days Present</p>
        </div>
        <div className="card text-center">
          <p className="text-2xl font-bold text-green-600">{formatCurrency(totalCollected)}</p>
          <p className="text-sm text-gray-500 mt-1">Total Paid</p>
        </div>
        <div className="card text-center">
          <p className="text-3xl font-bold text-purple-600">{supplementSales.length}</p>
          <p className="text-sm text-gray-500 mt-1">Supplement Purchases</p>
        </div>
      </div>

      {/* ── PAYMENT HISTORY ── prominent section */}
      <div className="card">
        <div className="flex items-center justify-between mb-5">
          <div>
            <h3 className="text-lg font-bold text-gray-800">💳 Payment History</h3>
            <p className="text-xs text-gray-500 mt-0.5">{payments.length} transaction{payments.length !== 1 ? 's' : ''} • Total collected: {formatCurrency(totalCollected)}</p>
          </div>
          <button onClick={openQuickPay} className="btn-success text-sm">+ Record Payment</button>
        </div>

        {payments.length === 0 ? (
          <div className="text-center py-10 bg-gray-50 rounded-xl">
            <div className="text-4xl mb-2">💳</div>
            <p className="text-gray-500 font-medium">No payments recorded yet</p>
            <p className="text-gray-400 text-sm mt-1">Click "Record Payment" to add the first payment</p>
          </div>
        ) : (
          <div className="space-y-3">
            {payments.map((p, i) => (
              <PaymentCard key={p._id} payment={p} index={i} onSettle={openSettle} />
            ))}
          </div>
        )}
      </div>

      {/* Attendance dots */}
      <div className="card">
        <h3 className="font-semibold text-gray-700 mb-4">📅 Recent Attendance (last 60 records)</h3>
        {attendance.length === 0 ? (
          <p className="text-gray-400 text-sm text-center py-4">No attendance records</p>
        ) : (
          <div className="flex flex-wrap gap-1.5">
            {attendance.map((a, i) => {
              const bs = adToBS(a.date);
              const label = bs ? `${bs.day} ${NEPALI_MONTHS[bs.month]} ${bs.year} — ${a.shift}` : `${a.date} — ${a.shift}`;
              return (
                <div key={i} title={label}
                  className={`w-7 h-7 rounded text-xs flex items-center justify-center font-bold cursor-default ${a.checkedIn ? 'bg-green-100 text-green-700' : 'bg-red-50 text-red-300'}`}>
                  {a.checkedIn ? '✓' : '✗'}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Supplement Sales */}
      {supplementSales.length > 0 && (
        <div className="card">
          <h3 className="font-semibold text-gray-700 mb-4">💊 Supplement Purchases</h3>
          <div className="space-y-2">
            {supplementSales.map(s => (
              <div key={s._id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div>
                  <p className="text-sm font-medium text-gray-800">{s.supplementId?.name} ({s.supplementId?.brand})</p>
                  <p className="text-xs text-gray-500">Qty: {s.quantity} • {formatNepaliDate(s.saleDate)}</p>
                </div>
                <p className="text-sm font-semibold text-gray-700">{formatCurrency(s.totalAmount)}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── Quick Pay Modal ── */}
      <Modal isOpen={quickPayModal} onClose={() => setQuickPayModal(false)} title="Record Payment" size="sm">
        {!calcData ? (
          <LoadingSpinner message="Calculating amount..." />
        ) : (
          <div className="space-y-4">
            <div className="p-3 bg-blue-50 rounded-xl text-center">
              <p className="text-xs text-gray-500 mb-1">{calcData.isFirstPayment ? 'First Payment (Admission + Plan)' : `${member.plan} Renewal`}</p>
              <p className="text-3xl font-bold text-blue-600">{formatCurrency(quickPayable)}</p>
            </div>

            {/* Breakdown */}
            <div className="rounded-xl border border-gray-100 overflow-hidden text-sm">
              {calcData.isFirstPayment && (
                <div className="flex justify-between px-4 py-2.5 border-b border-gray-100">
                  <span className="text-gray-500">Admission Fee</span>
                  <span className="font-medium">{formatCurrency(calcData.admissionFee)}</span>
                </div>
              )}
              <div className="flex justify-between px-4 py-2.5 border-b border-gray-100">
                <span className="text-gray-500">
                  {member.plan} Fee
                  <span className="text-xs text-gray-400 ml-1">({calcData.months} mo × {formatCurrency(calcData.monthlyFee)})</span>
                </span>
                <span className="font-medium">{formatCurrency(calcData.planFee)}</span>
              </div>
              {showDiscountInModal && parseFloat(discount) > 0 && (
                <div className="flex justify-between px-4 py-2.5 border-b border-gray-100 text-green-600">
                  <span>Discount</span>
                  <span>− {formatCurrency(parseFloat(discount))}</span>
                </div>
              )}
              <div className="flex justify-between px-4 py-3 bg-blue-50 font-bold">
                <span>Total Payable</span>
                <span className="text-blue-600 text-lg">{formatCurrency(quickPayable)}</span>
              </div>
            </div>

            {/* New expiry preview — only when paying in full */}
            {calcData.newExpiry && !isPartial && (
              <div className="flex items-center gap-3 px-4 py-3 bg-green-50 border border-green-200 rounded-xl">
                <span className="text-green-600 text-lg">📅</span>
                <div>
                  <p className="text-xs text-gray-500 font-medium">New Expiry After Payment</p>
                  <p className="text-sm font-bold text-green-700">{formatNepaliDate(calcData.newExpiry)}</p>
                </div>
              </div>
            )}
            {isPartial && (
              <div className="flex items-center gap-2 px-3 py-2 bg-yellow-50 border border-yellow-200 rounded-xl text-xs text-yellow-700">
                <span>⚠️</span>
                <span>Expiry date will <strong>not</strong> change until remaining balance is paid.</span>
              </div>
            )}

            {showDiscountInModal && (
              <div>
                <label className="label">Discount (₹) — optional for {member.plan}</label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 font-bold text-sm">₹</span>
                  <input className="input pl-7" type="number" min="0" value={discount} onChange={e => setDiscount(e.target.value)} placeholder="0" />
                </div>
              </div>
            )}

            {/* Partial payment toggle */}
            <div className="flex items-center justify-between p-3 bg-gray-50 rounded-xl">
              <div>
                <p className="text-sm font-medium text-gray-700">Partial Payment</p>
                <p className="text-xs text-gray-400">Collect less than the full amount</p>
              </div>
              <button
                type="button"
                onClick={() => { setIsPartial(v => !v); setPartialAmount(''); }}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${isPartial ? 'bg-yellow-500' : 'bg-gray-300'}`}
              >
                <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${isPartial ? 'translate-x-6' : 'translate-x-1'}`} />
              </button>
            </div>

            {isPartial && (
              <div>
                <label className="label">Amount Being Paid Now (₹)</label>
                <input
                  className="input"
                  type="number"
                  min="1"
                  max={quickPayable - 1}
                  value={partialAmount}
                  onChange={e => setPartialAmount(e.target.value)}
                  placeholder={`Max ₹${quickPayable - 1}`}
                  autoFocus
                />
                {partialAmount && (
                  <p className="text-xs text-yellow-600 mt-1">
                    Balance remaining: {formatCurrency(Math.max(0, quickPayable - (parseFloat(partialAmount) || 0)))}
                  </p>
                )}
              </div>
            )}

            <div>
              <label className="label">Payment Mode</label>
              <div className="flex gap-2">
                {['Cash', 'UPI', 'Online'].map(m => (
                  <button key={m} type="button" onClick={() => setPayMode(m)}
                    className={`flex-1 py-2 rounded-lg text-sm font-medium border-2 transition-all ${payMode === m ? 'border-blue-500 bg-blue-50 text-blue-700' : 'border-gray-200 text-gray-500'}`}>
                    {m === 'Cash' ? '💵' : m === 'UPI' ? '📱' : '🏦'} {m}
                  </button>
                ))}
              </div>
            </div>

            <div className="flex gap-3 pt-1">
              <button onClick={() => setQuickPayModal(false)} className="btn-secondary flex-1">Cancel</button>
              <button onClick={handleQuickPay} className={`flex-1 ${isPartial ? 'bg-yellow-500 hover:bg-yellow-600 text-white rounded-lg font-medium py-2 text-sm transition-colors' : 'btn-success'}`}>
                {isPartial
                  ? `💰 Collect ${partialAmount ? formatCurrency(parseFloat(partialAmount)) : '...'}`
                  : `✓ Collect ${formatCurrency(quickPayable)}`}
              </button>
            </div>
          </div>
        )}
      </Modal>

      {/* Renew Modal */}
      <Modal isOpen={renewModal} onClose={() => setRenewModal(false)} title="Renew Membership">
        <div className="space-y-4">
          <div>
            <label className="label">New Plan</label>
            <select className="input" value={renewForm.plan} onChange={e => setRenewForm(f => ({ ...f, plan: e.target.value }))}>
              <option>Monthly</option>
              <option>Quarterly</option>
              <option>Yearly</option>
            </select>
          </div>
          {pricing && (
            <div className="p-3 bg-gray-50 rounded-xl text-sm">
              <p className="text-gray-500">Renewal amount (no admission fee):</p>
              <p className="text-xl font-bold text-blue-600 mt-1">
                {formatCurrency(pricing.monthlyFee * (renewForm.plan === 'Monthly' ? 1 : renewForm.plan === 'Quarterly' ? 3 : 12))}
              </p>
            </div>
          )}
          <div>
            <label className="label">Payment Amount (₹)</label>
            <input className="input" type="number" value={renewForm.amount} onChange={e => setRenewForm(f => ({ ...f, amount: e.target.value }))} placeholder="Enter amount" />
          </div>
          <div>
            <label className="label">Payment Mode</label>
            <div className="flex gap-2">
              {['Cash', 'UPI', 'Online'].map(m => (
                <button key={m} type="button" onClick={() => setRenewForm(f => ({ ...f, paymentMode: m }))}
                  className={`flex-1 py-2 rounded-lg text-sm font-medium border-2 transition-all ${renewForm.paymentMode === m ? 'border-blue-500 bg-blue-50 text-blue-700' : 'border-gray-200 text-gray-500'}`}>
                  {m === 'Cash' ? '💵' : m === 'UPI' ? '📱' : '🏦'} {m}
                </button>
              ))}
            </div>
          </div>
          <div className="flex gap-3 justify-end pt-2">
            <button onClick={() => setRenewModal(false)} className="btn-secondary">Cancel</button>
            <button onClick={handleRenew} className="btn-primary">🔄 Renew Membership</button>
          </div>
        </div>
      </Modal>

      {/* ── Settle Partial Payment Modal ── */}
      <Modal isOpen={settleModal} onClose={() => setSettleModal(false)} title="Pay Remaining Balance" size="sm">
        {settlePayment && (
          <div className="space-y-4">
            <div className="p-3 bg-yellow-50 rounded-xl text-sm">
              <div className="flex justify-between">
                <span className="text-gray-500">Total due</span>
                <span className="font-semibold">{formatCurrency(settlePayment.amount)}</span>
              </div>
              <div className="flex justify-between mt-1">
                <span className="text-gray-500">Already paid</span>
                <span className="font-semibold text-green-600">{formatCurrency(settlePayment.amountPaid)}</span>
              </div>
              <div className="flex justify-between mt-1 pt-1 border-t border-yellow-200">
                <span className="font-bold text-gray-700">Balance remaining</span>
                <span className="font-bold text-red-500">{formatCurrency(settlePayment.balanceRemaining)}</span>
              </div>
            </div>

            <div>
              <label className="label">Amount to Pay (₹)</label>
              <input
                className="input"
                type="number"
                min="1"
                max={settlePayment.balanceRemaining}
                value={settleAmount}
                onChange={e => setSettleAmount(e.target.value)}
              />
              {parseFloat(settleAmount) >= settlePayment.balanceRemaining && (
                <p className="text-xs text-green-600 mt-1 font-medium">✓ Full balance — expiry date will be extended</p>
              )}
            </div>

            <div>
              <label className="label">Payment Mode</label>
              <div className="flex gap-2">
                {['Cash', 'UPI', 'Online'].map(m => (
                  <button key={m} type="button" onClick={() => setSettleMode(m)}
                    className={`flex-1 py-2 rounded-lg text-sm font-medium border-2 transition-all ${settleMode === m ? 'border-yellow-500 bg-yellow-50 text-yellow-700' : 'border-gray-200 text-gray-500'}`}>
                    {m === 'Cash' ? '💵' : m === 'UPI' ? '📱' : '🏦'} {m}
                  </button>
                ))}
              </div>
            </div>

            <div className="flex gap-3 pt-1">
              <button onClick={() => setSettleModal(false)} className="btn-secondary flex-1">Cancel</button>
              <button
                onClick={handleSettle}
                disabled={settleLoading}
                className="flex-1 text-sm px-4 py-2 rounded-lg font-medium bg-yellow-500 text-white hover:bg-yellow-600 disabled:opacity-50 transition-colors flex items-center justify-center gap-2"
              >
                {settleLoading
                  ? <><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> Processing...</>
                  : '💰 Record Payment'}
              </button>
            </div>
          </div>
        )}
      </Modal>

      {/* ── Supplement Sale Modal ── */}
      <Modal isOpen={supModal} onClose={() => setSupModal(false)} title="Sell Supplement to Member" size="sm">
        <div className="space-y-4">
          {/* Member reminder */}
          <div className="flex items-center gap-3 p-3 bg-purple-50 rounded-xl">
            <MemberAvatar photo={member.photo} name={member.name} size="sm" />
            <div>
              <p className="text-sm font-semibold text-gray-800">{member.name}</p>
              <p className="text-xs text-gray-500">{member.memberId}</p>
            </div>
          </div>

          {/* Supplement picker */}
          <div>
            <label className="label">Supplement *</label>
            <select
              className="input"
              value={supForm.supplementId}
              onChange={e => setSupForm(f => ({ ...f, supplementId: e.target.value }))}
            >
              <option value="">— Select supplement —</option>
              {supplements.map(s => (
                <option key={s._id} value={s._id}>
                  {s.name} ({s.brand}) — ₹{s.sellingPrice} — {s.stock} in stock
                </option>
              ))}
            </select>
            {supplements.length === 0 && (
              <p className="text-xs text-red-500 mt-1">No supplements in stock. Add stock from Supplements page.</p>
            )}
          </div>

          {/* Quantity */}
          <div>
            <label className="label">Quantity *</label>
            <input
              className="input"
              type="number"
              min="1"
              max={supplements.find(s => s._id === supForm.supplementId)?.stock || 99}
              value={supForm.quantity}
              onChange={e => setSupForm(f => ({ ...f, quantity: e.target.value }))}
            />
          </div>

          {/* Discount */}
          <div>
            <label className="label">Discount Amount (₹)</label>
            <input
              className="input"
              type="number"
              min="0"
              value={supForm.discount}
              onChange={e => setSupForm(f => ({ ...f, discount: e.target.value }))}
              placeholder="0"
            />
          </div>

          {/* Price summary */}
          {supForm.supplementId && supForm.quantity && (() => {
            const sel = supplements.find(s => s._id === supForm.supplementId);
            const qty = parseInt(supForm.quantity) || 0;
            if (!sel || qty < 1) return null;
            const subtotal = sel.sellingPrice * qty;
            const discount = parseFloat(supForm.discount) || 0;
            const total = Math.max(0, subtotal - discount);
            const profit = Math.max(0, (sel.sellingPrice - sel.purchasePrice) * qty - discount);
            return (
              <div className="rounded-xl overflow-hidden border border-purple-100">
                <div className="flex justify-between px-4 py-2.5 bg-gray-50 text-sm border-b border-gray-100">
                  <span className="text-gray-500">Unit price</span>
                  <span className="font-medium">{formatCurrency(sel.sellingPrice)}</span>
                </div>
                <div className="flex justify-between px-4 py-2.5 bg-gray-50 text-sm border-b border-gray-100">
                  <span className="text-gray-500">Quantity</span>
                  <span className="font-medium">× {qty}</span>
                </div>
                {discount > 0 && (
                  <>
                    <div className="flex justify-between px-4 py-2.5 bg-gray-50 text-sm border-b border-gray-100">
                      <span className="text-gray-500">Subtotal</span>
                      <span className="font-medium">{formatCurrency(subtotal)}</span>
                    </div>
                    <div className="flex justify-between px-4 py-2.5 bg-red-50 text-sm border-b border-gray-100">
                      <span className="text-red-500">Discount</span>
                      <span className="font-medium text-red-500">− {formatCurrency(discount)}</span>
                    </div>
                  </>
                )}
                <div className="flex justify-between px-4 py-3 bg-purple-50">
                  <span className="font-bold text-gray-800">Total to Collect</span>
                  <span className="font-bold text-xl text-purple-600">{formatCurrency(total)}</span>
                </div>
                <div className="flex justify-between px-4 py-2 bg-green-50 text-xs text-green-600">
                  <span>Your profit on this sale</span>
                  <span className="font-semibold">{formatCurrency(profit)}</span>
                </div>
              </div>
            );
          })()}

          <div className="flex gap-3 pt-1">
            <button onClick={() => setSupModal(false)} className="btn-secondary flex-1">Cancel</button>
            <button
              onClick={handleSupSell}
              disabled={supLoading || !supForm.supplementId}
              className="flex-1 text-sm px-4 py-2 rounded-lg font-medium bg-purple-600 text-white hover:bg-purple-700 disabled:opacity-50 transition-colors flex items-center justify-center gap-2"
            >
              {supLoading
                ? <><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> Processing...</>
                : '💊 Record Sale'}
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
}

export default MemberProfile;
